import { addDays, addMonths, startOfDay } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'urql';
import { SCHEDULE_DATE_RANGE_FILTER } from '~/config/scheduleFeatureFlags';
import { ScheduleDocument, ScheduleQuery } from '~/gql/graphql';
import { registerScheduleVariables } from '~/helpers/scheduleQueryVariables';

/**
 * Configuration for the initial date range
 */
interface RangeConfig {
  /** Days before today for initial load (default: 14) */
  initialDaysBefore?: number;
  /** Months after today for initial load (default: 2) */
  initialMonthsAfter?: number;
  /** Days to load when extending backward */
  extensionDaysBefore?: number;
  /** Days to load when extending forward */
  extensionDaysAfter?: number;
  /** Days from loaded boundary to trigger extension */
  extensionThreshold?: number;
}

interface UseScheduleRangeResult {
  /** Current merged data from all loaded ranges */
  data: ScheduleQuery | undefined;
  /** Whether any query is currently fetching */
  fetching: boolean;
  /** Any error from the queries */
  error: Error | undefined;
  /** The currently loaded date range bounds */
  loadedBounds: { start: Date; end: Date } | null;
  /** Request to extend the loaded range in a direction */
  extendRange: (direction: 'backward' | 'forward') => void;
  /**
   * Check if a date needs range adjustment and handle it.
   * For dates near boundaries: extends incrementally.
   * For dates far outside: re-centers the range around the target date.
   * Returns whether an adjustment was triggered.
   */
  ensureDateInRange: (date: Date) => boolean;
  /** Force refetch the current range */
  refetch: () => void;
}

/**
 * Hook for range-aware schedule data loading.
 *
 * Implements efficient loading strategy:
 * - Initial load: 2 weeks before today + 2 months after
 * - As user navigates, extends the range forward/backward
 * - Uses urql's cache-and-network to batch requests and cache results
 */
export function useScheduleRange(
  config: RangeConfig = {}
): UseScheduleRangeResult {
  const {
    initialDaysBefore = 7,
    initialMonthsAfter = 1,
    extensionDaysBefore = 15,
    extensionDaysAfter = 30,
    extensionThreshold = 7,
  } = config;

  // Track the overall loaded range bounds
  const [loadedStart, setLoadedStart] = useState<Date>(() =>
    startOfDay(addDays(new Date(), -initialDaysBefore))
  );
  const [loadedEnd, setLoadedEnd] = useState<Date>(() =>
    startOfDay(addMonths(new Date(), initialMonthsAfter))
  );

  // Track pending extensions to prevent duplicate requests
  const pendingExtensionRef = useRef<'backward' | 'forward' | null>(null);

  // Convert dates to ISO strings for the query
  const startISO = useMemo(() => loadedStart.toISOString(), [loadedStart]);
  const endISO = useMemo(() => loadedEnd.toISOString(), [loadedEnd]);

  // Main query with the current range
  // Currently SCHEDULE_DATE_RANGE_FILTER is disabled - query runs without date filters
  // TODO: When backend supports start/end on ScheduleFilters:
  // 1. Uncomment ScheduleDocumentWithDateRange in Schedule.ts
  // 2. Run codegen
  // 3. Set SCHEDULE_DATE_RANGE_FILTER = true
  // 4. Update this to use ScheduleDocumentWithDateRange with { start, end } variables
  const [{ data, fetching, error }, reexecuteQuery] = useQuery({
    query: ScheduleDocument,
    requestPolicy: 'cache-and-network',
  });

  // Register the current variables for cache operations to find
  // Only needed when date range filtering is enabled (creates multiple cache entries)
  // Currently disabled since backend doesn't support date range filtering yet
  useEffect(() => {
    if (SCHEDULE_DATE_RANGE_FILTER) {
      registerScheduleVariables({ start: startISO, end: endISO });
    }
  }, [startISO, endISO]);

  // Extend the loaded range in a direction
  const extendRange = useCallback(
    (direction: 'backward' | 'forward') => {
      // Prevent duplicate extensions while one is pending
      if (pendingExtensionRef.current === direction) {
        return;
      }

      pendingExtensionRef.current = direction;

      if (direction === 'backward') {
        setLoadedStart((prev) => {
          const newStart = startOfDay(addDays(prev, -extensionDaysBefore));
          // Clear pending flag after state update
          requestAnimationFrame(() => {
            pendingExtensionRef.current = null;
          });
          return newStart;
        });
      } else {
        setLoadedEnd((prev) => {
          const newEnd = startOfDay(addDays(prev, extensionDaysAfter));
          // Clear pending flag after state update
          requestAnimationFrame(() => {
            pendingExtensionRef.current = null;
          });
          return newEnd;
        });
      }
    },
    [extensionDaysBefore, extensionDaysAfter]
  );

  /**
   * Ensures the given date is within (or near) the loaded range.
   * - If the date is near boundaries: extends incrementally (for smooth scrolling)
   * - If the date is far outside: re-centers the range around it (for large jumps)
   * This avoids loading all the data between current range and target date.
   */
  const ensureDateInRange = useCallback(
    (date: Date): boolean => {
      const dateTime = date.getTime();
      const loadedStartTime = loadedStart.getTime();
      const loadedEndTime = loadedEnd.getTime();

      const thresholdMs = extensionThreshold * 24 * 60 * 60 * 1000;
      // If jumping more than 60 days outside the range, re-center instead of extending
      const jumpThresholdMs = 60 * 24 * 60 * 60 * 1000;

      // Check if date is far outside the loaded range (large jump)
      const isFarBefore = loadedStartTime - dateTime > jumpThresholdMs;
      const isFarAfter = dateTime - loadedEndTime > jumpThresholdMs;

      if (isFarBefore || isFarAfter) {
        // Re-center the range around the target date instead of extending incrementally
        // This loads data around where the user wants to be, not everything in between
        const newStart = startOfDay(addDays(date, -initialDaysBefore));
        const newEnd = startOfDay(addMonths(date, initialMonthsAfter));

        setLoadedStart(newStart);
        setLoadedEnd(newEnd);
        return true;
      }

      // Check if near or slightly outside the start boundary - extend backward
      if (
        dateTime < loadedStartTime ||
        dateTime - loadedStartTime < thresholdMs
      ) {
        extendRange('backward');
        return true;
      }

      // Check if near or slightly outside the end boundary - extend forward
      if (dateTime > loadedEndTime || loadedEndTime - dateTime < thresholdMs) {
        extendRange('forward');
        return true;
      }

      // Date is comfortably within the loaded range
      return false;
    },
    [
      loadedStart,
      loadedEnd,
      extensionThreshold,
      initialDaysBefore,
      initialMonthsAfter,
      extendRange,
    ]
  );

  // Force refetch
  const refetch = useCallback(() => {
    reexecuteQuery({ requestPolicy: 'network-only' });
  }, [reexecuteQuery]);

  // Compute loaded bounds for consumers
  const loadedBounds = useMemo(
    () => ({
      start: loadedStart,
      end: loadedEnd,
    }),
    [loadedStart, loadedEnd]
  );

  return {
    data,
    fetching,
    error: error as Error | undefined,
    loadedBounds,
    extendRange,
    ensureDateInRange,
    refetch,
  };
}
